import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Input } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { makeStyles } from '@material-ui/styles';

import Modal from 'common/components/modal';

const useStyles = makeStyles(theme => ({
  modalWrapper: ({ error, descError }) => ({
    '& .ant-modal-body': {
      padding: '0 32px 32px',

      '& .ant-descriptions-item-label': {
        padding: '9.25px 17px',
        color: theme.colors.bodyColor,
        backgroundColor: theme.colors.gray3,
      },

      '& .ant-descriptions-item-content': {
        color: theme.colors.bodyColor,
        padding: '9.25px 17px',
      },
    },

    '& .ant-modal-footer': {
      '& .ant-btn-background-ghost': {
        borderColor: theme.colors.gray5,
        color: theme.colors.bodyColor,
        fontFamily: "'Avenir Next W04', 'Avenir Next', helvetica, arial, sans-serif",
        boxShadow: 'none',

        '& span': {
          fontWeight: 400,
        },
      },
    },

    '& .input-container': {
      '& .ant-input': {
        padding: '5px 11px',
        borderColor: error ? theme.colors.red11 : theme.colors.gray5,
        '& :hover': {
          borderColor: error ? theme.colors.red11 : theme.colors.gray5,
        },
      },
      '& .ant-input:focus': {
        boxShadow: 'none',
      },
    },

    '& .textarea-container': {
      '& .ant-input': {
        padding: '5px 11px',
        borderColor: descError ? theme.colors.red11 : theme.colors.gray5,
        '& :hover': {
          borderColor: descError ? theme.colors.red11 : theme.colors.gray5,
        },
      },
      '& .ant-input:focus': {
        boxShadow: 'none',
      },
    },
  }),

  desc: {
    ...theme.typography.body2,
    fontFamily: "'Avenir Next W04', 'Avenir Next', helvetica, arial, sans-serif",
    display: 'inline-block',
    marginBottom: 20,
    marginTop: -4,
    color: theme.colors.bodyColor,
  },

  label: {
    ...theme.typography.body2,
    ...theme.flex.crossCenter,
    fontFamily: "'Avenir Next W04', 'Avenir Next', helvetica, arial, sans-serif",
    display: 'inline-block',
    marginBottom: 8,
    color: theme.colors.titleColor,
  },

  required: {
    color: theme.colors.red5,
    marginRight: 4,
  },

  labelNotice: {
    ...theme.flex.row,
    padding: '6px 12px',
    borderRadius: 2,
    backgroundColor: theme.colors.gray3,
    marginTop: 8,
    marginBottom: 24,
  },

  labelIcon: {
    width: 14,
    height: 14,
    marginRight: 6,
    marginTop: 4,
    color: theme.colors.captionColor,
  },

  labelNoticeContent: {
    ...theme.typography.body2,
    fontFamily: "'Avenir Next W04', 'Avenir Next', helvetica, arial, sans-serif",
    color: theme.colors.captionColor,
    margin: 0,
  },

  count: {
    ...theme.flex.mainEnd,
    ...theme.typography.body2,
    fontFamily: "'Avenir Next W04', 'Avenir Next', helvetica, arial, sans-serif",
    color: theme.colors.disableColor,
  },

  errorTip: {
    ...theme.typography.body2,
    fontFamily: "'Avenir Next W04', 'Avenir Next', helvetica, arial, sans-serif",
    color: theme.colors.red11,
    margin: 0,
    marginTop: 4,
  },
}));

const CreateTokenModal = ({
  t,
  handleCreateTokenApi,
  visible,
  handleOpenOrCloseModal,
  hasAppliedAPItoken,
  handleUpdateTokenApi,
}) => {
  const [label, setLabel] = useState();
  const [description, setDescription] = useState();
  const [error, setError] = useState(false);
  const [lengthError, setLengthError] = useState(false);
  const [descError, setDescError] = useState(false);
  const [descriptionCount, setDescriptionCount] = useState(0);
  const classes = useStyles({ error, descError });

  const handleCallback = () => {
    if (hasAppliedAPItoken) {
      handleUpdateTokenApi(label, description);
    } else {
      handleCreateTokenApi(label, description);
    }
  };

  const handleOnLabelChange = e => {
    const inputValue = e.target.value;
    if (inputValue?.length > 20) {
      setLengthError(true);
      setError(true);
    } else if (!inputValue) {
      setLengthError(false);
      setError(true);
    } else {
      setError(false);
      setLengthError(false);
      setLabel(inputValue);
    }
  };

  const handleOnDescChange = e => {
    const textareaValue = e.target.value;
    setDescriptionCount(textareaValue?.length);

    if (textareaValue?.length > 50) {
      setDescError(true);
    } else {
      setDescError(false);
      setDescription(textareaValue);
    }
  };

  return (
    <Modal
      visible={visible}
      okText={t('cms.properties.edit.leave_alert.title')}
      width={454}
      title={t('cms.landlord.token.create_modal.title')}
      wrapClassName={classes.modalWrapper}
      onCancel={handleOpenOrCloseModal}
      onOk={handleCallback}
      okButtonProps={{ disabled: error || !label || lengthError || descError }}
    >
      <span className={classes.desc}>
        {t('cms.dashboard.landlord.details.token.create_token_modal.desc')}
      </span>
      <div className="input-container">
        <span className={classes.label}>
          <span className={classes.required}>*</span>
          {t('cms.dashboard.landlord.details.token.create_token_modal.field_label')}
        </span>
        <Input onChange={handleOnLabelChange} onBlur={handleOnLabelChange} />
        <If condition={error}>
          <p className={classes.errorTip}>
            {lengthError
              ? t('cms.dashboard.landlord.details.token.create_token_modal.input_limit', {
                  count: 20,
                })
              : t('cms:cms.referral.commission_plan.modal.error.invalid')}
          </p>
        </If>
        <div className={classes.labelNotice}>
          <InfoCircleOutlined className={classes.labelIcon} />
          <p className={classes.labelNoticeContent}>
            {t('cms.dashboard.landlord.details.token.create_token_modal.label_desc')}
          </p>
        </div>
      </div>
      <div className="textarea-container">
        <span className={classes.label}>
          {t('cms.dashboard.landlord.details.token.create_token_modal.field_desc')}
        </span>
        <Input.TextArea
          style={{ height: 70 }}
          placeholder={t(
            'cms.dashboard.landlord.details.token.create_token_modal.desc_placeholder',
          )}
          onChange={handleOnDescChange}
          onBlur={handleOnDescChange}
        />
        <If condition={descError}>
          <p className={classes.errorTip}>
            {t('cms.dashboard.landlord.details.token.create_token_modal.input_limit', {
              count: 50,
            })}
          </p>
        </If>
        <div className={classes.count}>{descriptionCount} / 50</div>
      </div>
    </Modal>
  );
};

CreateTokenModal.propTypes = {
  t: PropTypes.func.isRequired,
  handleCreateTokenApi: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  handleOpenOrCloseModal: PropTypes.func.isRequired,
};

export default CreateTokenModal;
